import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  FileText, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Eye,
  Calendar,
  User,
  DollarSign,
  Send,
  RefreshCw,
  Check,
  X,
  Filter,
  Search,
  ChevronDown,
  ChevronRight,
  History,
  Loader2
} from 'lucide-react';
import { useAuth } from '../components/AuthProvider';
import { purchaseRequestApiService, PurchaseRequest } from '../services/purchaseRequestApi';
import { promotionApiService } from '../services/promotionApi';

const ClientInvoicesPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invoices, setInvoices] = useState<PurchaseRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingAction, setProcessingAction] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedInvoice, setExpandedInvoice] = useState<number | null>(null);

  // Fonctions utilitaires
  const loadInvoices = useCallback(async () => {
    try {
      setLoading(true);
      console.log('📥 Chargement des factures...');
      console.log('👤 Utilisateur:', user);
      console.log('🔑 Token:', localStorage.getItem('access_token')?.substring(0, 50) + '...');
      
      if (!user?.id) {
        console.error('❌ Utilisateur non connecté');
        return;
      }
      
      const data = await purchaseRequestApiService.getUserPurchaseRequests();
      console.log('✅ Factures chargées:', data);
      console.log('📊 Nombre de factures:', Array.isArray(data) ? data.length : 'Pas un array');
      
      // Debug de l'historique
      if (Array.isArray(data)) {
        data.forEach((invoice, index) => {
          console.log(`📋 Facture ${index + 1}:`, {
            id: invoice.id,
            invoice_id: invoice.invoice_id,
            status: invoice.status,
            timeline_count: invoice.timeline?.length || 0,
            latest_update: invoice.timeline?.[invoice.timeline?.length - 1]?.date
          });
        });
      }
      
      setInvoices(data);
    } catch (error) {
      console.error('❌ Erreur lors du chargement des factures:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  // Recharger les factures quand on revient sur la page
  useEffect(() => {
    const handleFocus = () => {
      console.log('🔄 Page focus - Rechargement des factures...');
      loadInvoices();
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('🔄 Page visible - Rechargement des factures...');
        loadInvoices();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadInvoices]);

  const confirmInvoice = async (invoiceId: number) => {
    try {
      setProcessingAction(invoiceId.toString());
      
      // Trouver la facture pour récupérer le code promo
      const invoice = invoices.find(inv => inv.id === invoiceId);
      
      // Si un code promo est utilisé, le désactiver
      if (invoice?.promo_code) {
        try {
          console.log('🎁 Désactivation du code promo:', invoice.promo_code);
          await promotionApiService.deactivateUserPromoCode(invoice.promo_code);
          console.log('✅ Code promo désactivé avec succès');
        } catch (promoError) {
          console.error('⚠️ Erreur lors de la désactivation du code promo:', promoError);
          // Continuer malgré l'erreur de désactivation du code promo
        }
      }
      
      // Confirmer la facture
      await purchaseRequestApiService.updatePurchaseRequestStatus(invoiceId.toString(), 'confirmed');
      await loadInvoices();
      // Notification de succès
    } catch (error) {
      console.error('Erreur lors de la confirmation:', error);
    } finally {
      setProcessingAction(null);
    }
  };

  const cancelInvoice = async (invoiceId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler cette facture ?')) {
      return;
    }

    try {
      setProcessingAction(invoiceId.toString());
      
      // Trouver la facture pour récupérer le code promo et l'historique
      const invoice = invoices.find(inv => inv.id === invoiceId);
      
      // Vérifier si la facture a déjà été confirmée dans le passé
      const wasEverConfirmed = invoice?.timeline?.some(
        (entry: any) => entry.action === 'client_confirmation'
      ) || false;
      
      // Annuler la facture
      await purchaseRequestApiService.updatePurchaseRequestStatus(invoiceId.toString(), 'cancelled');
      
      // Si un code promo est utilisé ET la facture n'a jamais été confirmée, le réactiver
      if (invoice?.promo_code && !wasEverConfirmed) {
        try {
          console.log('🔄 Réactivation du code promo:', invoice.promo_code);
          await promotionApiService.reactivatePromoCode(invoice.promo_code);
          console.log('✅ Code promo réactivé avec succès');
        } catch (promoError) {
          console.error('⚠️ Erreur lors de la réactivation du code promo:', promoError);
          // Continuer malgré l'erreur
        }
      } else if (invoice?.promo_code && wasEverConfirmed) {
        console.log('⚠️ Code promo non réactivé car facture déjà confirmée dans le passé');
      }
      
      await loadInvoices();
    } catch (error) {
      console.error('Erreur lors de l\'annulation:', error);
    } finally {
      setProcessingAction(null);
    }
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'pending':
        return {
          label: 'En attente',
          color: 'bg-amber-50 text-amber-700 border-amber-200',
          icon: Clock,
          bgColor: 'bg-amber-500',
          description: 'Votre demande est en cours de traitement'
        };
      case 'revision':
        return {
          label: 'Révisée - Action requise',
          color: 'bg-blue-50 text-blue-700 border-blue-200',
          icon: RefreshCw,
          bgColor: 'bg-blue-500',
          description: 'Facture révisée par notre équipe'
        };
      case 'confirmed':
        return {
          label: 'Confirmée',
          color: 'bg-green-50 text-green-700 border-green-200',
          icon: CheckCircle,
          bgColor: 'bg-green-500',
          description: 'Facture confirmée'
        };
      case 'cancelled':
        return {
          label: 'Annulée',
          color: 'bg-red-50 text-red-700 border-red-200',
          icon: XCircle,
          bgColor: 'bg-red-500',
          description: 'Facture annulée'
        };
      default:
        return {
          label: 'Inconnu',
          color: 'bg-gray-50 text-gray-700 border-gray-200',
          icon: AlertTriangle,
          bgColor: 'bg-gray-500',
          description: 'Statut inconnu'
        };
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0
    }).format(price);
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Date inconnue';
    
    let isoDateString = dateString;
    if (dateString.includes(' ') && !dateString.includes('T')) {
      isoDateString = dateString.replace(' ', 'T') + 'Z';
    }
    
    const date = new Date(isoDateString);
    if (isNaN(date.getTime())) {
      return 'Date invalide';
    }
    
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimelineSteps = (invoice: PurchaseRequest) => {
    if (invoice.timeline && invoice.timeline.length > 0) {
      return invoice.timeline.map((entry, index) => {
        const isLast = index === invoice.timeline!.length - 1;
        const isCurrent = isLast && (entry.status === 'revision' || entry.status === 'pending');
        
        let icon = Clock;
        if (entry.action === 'created') icon = Send;
        else if (entry.action === 'admin_revision') icon = RefreshCw;
        else if (entry.action === 'client_confirmation') icon = CheckCircle;
        else if (entry.action === 'client_cancellation') icon = XCircle;
        
        let status = 'completed';
        if (isCurrent) {
          status = entry.status === 'cancelled' ? 'cancelled' : 'current';
        } else if (entry.status === 'cancelled') {
          status = 'cancelled';
        }
        
        let title = 'Étape';
        let description = entry.comment || '';
        
        if (entry.action === 'created') {
          title = 'Demande envoyée';
          description = 'Votre demande a été soumise';
        } else if (entry.action === 'admin_revision') {
          title = 'Facture révisée';
          description = 'Notre équipe a révisé votre facture';
        } else if (entry.action === 'client_confirmation') {
          title = 'Facture confirmée';
          description = 'Vous avez confirmé la facture';
        } else if (entry.action === 'client_cancellation') {
          title = 'Facture annulée';
          description = 'Vous avez annulé la facture';
        }

        return {
          title,
          description,
          date: entry.date,
          status,
          icon
        };
      });
    }

    // Fallback
    return [{
      title: 'Demande envoyée',
      description: 'Votre demande a été soumise',
      date: invoice.created_at || new Date().toISOString(),
      status: 'completed',
      icon: Send
    }];
  };

  // Filtrage des factures
  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = invoice.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         invoice.invoice_id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Chargement de vos factures...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header moderne */}
      <div className="bg-white shadow-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors duration-200 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="font-medium">Retour</span>
              </button>
              
              <div className="flex items-center space-x-3">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Mes Factures</h1>
                  <p className="text-sm text-gray-500">{filteredInvoices.length} facture(s)</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <button
                onClick={loadInvoices}
                disabled={loading}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span>Actualiser</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Recherche */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Rechercher par nom ou numéro de facture..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              />
            </div>
            
            {/* Filtre par statut */}
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-3 pr-10 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              >
                <option value="all">Tous les statuts</option>
                <option value="pending">En attente</option>
                <option value="revision">Révisée</option>
                <option value="confirmed">Confirmée</option>
                <option value="cancelled">Annulée</option>
              </select>
              <Filter className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Liste des factures */}
        {filteredInvoices.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Aucune facture trouvée</h3>
            <p className="text-gray-500">
              {searchTerm || statusFilter !== 'all' 
                ? 'Aucune facture ne correspond à vos critères de recherche.'
                : 'Vous n\'avez pas encore de factures.'
              }
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <AnimatePresence>
              {filteredInvoices.map((invoice, index) => {
                const statusInfo = getStatusInfo(invoice.status);
                const StatusIcon = statusInfo.icon;
                const timelineSteps = getTimelineSteps(invoice);
                const isExpanded = expandedInvoice === invoice.id;

                return (
                  <motion.div
                    key={invoice.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-200"
                  >
                    {/* En-tête de la facture */}
                    <div className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className={`w-12 h-12 rounded-lg ${statusInfo.bgColor} flex items-center justify-center`}>
                            <StatusIcon className="w-6 h-6 text-white" />
                          </div>
                          
                          <div>
                            <div className="flex items-center space-x-3">
                              <h3 className="text-lg font-semibold text-gray-900">
                                Facture #{invoice.invoice_id || invoice.id}
                              </h3>
                              <span className={`px-3 py-1 rounded-full text-sm font-medium border ${statusInfo.color}`}>
                                {statusInfo.label}
                              </span>
                            </div>
                            <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500">
                              <span className="flex items-center space-x-1">
                                <User className="w-4 h-4" />
                                <span>{invoice.client_name}</span>
                              </span>
                              <span className="flex items-center space-x-1">
                                <Calendar className="w-4 h-4" />
                                <span>{formatDate(invoice.created_at)}</span>
                              </span>
                              <span className="flex items-center space-x-1">
                                <DollarSign className="w-4 h-4" />
                                <span className="font-semibold text-gray-900">{formatPrice(invoice.total_amount || 0)}</span>
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center space-x-3">
                          {/* Actions selon le statut */}
                          {invoice.status === 'revision' && (
                            <div className="flex space-x-2">
                              <button
                                onClick={() => invoice.id && confirmInvoice(invoice.id)}
                                disabled={processingAction === invoice.id?.toString()}
                                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 disabled:opacity-50"
                              >
                                {processingAction === invoice.id?.toString() ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Check className="w-4 h-4" />
                                )}
                                <span>Confirmer</span>
                              </button>
                              <button
                                onClick={() => invoice.id && cancelInvoice(invoice.id)}
                                disabled={processingAction === invoice.id?.toString()}
                                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 disabled:opacity-50"
                              >
                                <X className="w-4 h-4" />
                                <span>Annuler</span>
                              </button>
                            </div>
                          )}

                          {invoice.status === 'cancelled' && (
                            <button
                              onClick={() => invoice.id && confirmInvoice(invoice.id)}
                              disabled={processingAction === invoice.id?.toString()}
                              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 disabled:opacity-50"
                            >
                              {processingAction === invoice.id?.toString() ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Check className="w-4 h-4" />
                              )}
                              <span>Confirmer</span>
                            </button>
                          )}
                          
                          <button
                            onClick={() => setExpandedInvoice(isExpanded ? null : invoice.id || null)}
                            className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                          >
                            <History className="w-4 h-4" />
                            <span>Historique</span>
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Timeline expandable */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="border-t border-gray-200 bg-gray-50"
                        >
                          <div className="p-6">
                            <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                              <History className="w-4 h-4" />
                              <span>Historique de la facture</span>
                            </h4>
                            <div className="space-y-4">
                              {timelineSteps.map((step, stepIndex) => {
                                const StepIcon = step.icon;
                                const isLast = stepIndex === timelineSteps.length - 1;
                                
                                // Trouver le dernier index d'une révision
                                const lastRevisionIndex = timelineSteps.map((s, idx) => s.title === 'Facture révisée' ? idx : -1)
                                  .filter(idx => idx !== -1)
                                  .pop();
                                const isLastRevision = step.title === 'Facture révisée' && stepIndex === lastRevisionIndex;
                                
                                return (
                                  <div key={stepIndex} className="flex items-start space-x-3 relative">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                      step.status === 'completed' ? 'bg-green-100 text-green-600' :
                                      step.status === 'current' ? 'bg-blue-100 text-blue-600' :
                                      step.status === 'cancelled' ? 'bg-red-100 text-red-600' :
                                      'bg-gray-100 text-gray-400'
                                    }`}>
                                      <StepIcon className="w-4 h-4" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between">
                                        <p className={`text-sm font-medium ${
                                          step.status === 'completed' ? 'text-green-900' :
                                          step.status === 'current' ? 'text-blue-900' :
                                          step.status === 'cancelled' ? 'text-red-900' :
                                          'text-gray-500'
                                        }`}>
                                          {step.title}
                                        </p>
                                        <div className="flex items-center space-x-2">
                                          <p className="text-xs text-gray-500">
                                            {formatDate(step.date)}
                                          </p>
                                          {/* Bouton "Voir la facture" uniquement pour la dernière révision */}
                                          {isLastRevision && invoice.id && (
                                            <button
                                              onClick={() => navigate('/facture-proforma', { 
                                                state: { 
                                                  purchaseRequestId: invoice.id!.toString(),
                                                  viewMode: true,
                                                  invoiceData: invoice
                                                }
                                              })}
                                              className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-1"
                                            >
                                              <Eye className="w-3 h-3" />
                                              <span>Voir la facture</span>
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                      <p className="text-xs text-gray-600 mt-1">
                                        {step.description}
                                      </p>
                                    </div>
                                    {!isLast && (
                                      <div className="absolute left-4 mt-8 w-px h-4 bg-gray-300" />
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientInvoicesPage;
